import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./style.css";
import CircularProgress from "@mui/material/CircularProgress";
import { onboardingStatusAPI } from "../../../store/features/checkOnboardingStatus";
import { tabs, timeOptions } from "../../../constants/index";
import { ReactComponent as BlueArrow } from "../../../assets/images/ChatImages/bluearrow.svg";
import { ReactComponent as RedArrow } from "../../../assets/images/ChatImages/redarrow.svg";
import Heatmap from "../../../components/dashboardcomponents/Analytics/Overview//HeatMap";
import DonutChart from "../../../components/dashboardcomponents/Analytics/Overview/DonutChat";
import {
  getAnalyticsOverviewStats,
  setCurrentRange,
  setDateRange,
} from "../../../store/features/getAnalyticsOverviewStats";
import { getChatGraphStats } from "../../../store/features/getChatGraphStats";
import { getTicketGraphStats } from "../../../store/features/getTicketGraphStats";
import { getChatSatisfactionGraphStats } from "../../../store/features/getChatSatisfactionGraphStats";
import { getWebsiteTrafficGraphStats } from "../../../store/features/getWebsiteTrafficGraphStats";
import {
  getTodayHeatMapStats,
  getYesterdayHeatMapStats,
  getWeeklyHeatMapStats,
  getMonthlyHeatMapStats,
  getYearlyHeatMapStats,
} from "../../../store/features/getHeatMapStats";
import MainHeaderAnalytics from "../../../components/dashboardcomponents/Analytics/MainHeaderAnalytics";
import { useAnalyticsExport } from "../../../hooks/useAnalyticsExport";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";
import OverviewChart from "../../../components/dashboardcomponents/OverViewChart";
import MonthlyChatTicketHeatmap from "../../../components/dashboardcomponents/Analytics/Overview/MonthlyHeatMap";
import YearlyChatTicketHeatmap from "../../../components/dashboardcomponents/Analytics/Overview/YearlyHeatmap";
import TodayHeatMap from "../../../components/dashboardcomponents/Analytics/Overview/TodayHeatMap";
import YesterdayHeatMap from "../../../components/dashboardcomponents/Analytics/Overview/YesterdayHeatMap";
import CustomHeatMap from "../../../components/dashboardcomponents/Analytics/Overview/CustomHeatMap";
import { Box, Typography } from "@mui/material";
import TabNavigation from "../../../components/dashboardcomponents/Home/TabNavigation";
import BarChart from "../../../components/dashboardcomponents/Home/BarChart";

function MainAnalyticsOverview() {
  const [loading, isLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Total Chats");
  const [activeTimeFilter, setActiveTimeFilter] = useState("7 days");
  const [dataLoading, setDataLoading] = useState(false);

  const chatGraphData = useSelector((state) => state.chatGraphStats.data);
  const chatGraphLoading = useSelector((state) => state.chatGraphStats.loading);
  const ticketGraphData = useSelector((state) => state.ticketGraphStats?.data);
  const ticketGraphLoading = useSelector(
    (state) => state.ticketGraphStats?.loading
  );
  const chatSatisfactionData = useSelector(
    (state) => state.chatSatisfactionGraphStats?.data
  );
  const chatSatisfactionLoading = useSelector(
    (state) => state.chatSatisfactionGraphStats?.loading
  );
  const websiteTrafficData = useSelector(
    (state) => state.websiteTrafficGraphStats?.data
  );
  const websiteTrafficLoading = useSelector(
    (state) => state.websiteTrafficGraphStats?.loading
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage
  );
  const userData = useSelector((state) => state.loginToken);
  const {
    data: analyticsOverviewData,
    currentRange,
    dateRange,
  } = useSelector((state) => state.analyticsOverviewStats);
  const t = useSelector(
    (state) => state.language.translations[currentLanguage]
  );

  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();

  const {
    exportSectionOptions,
    exportSection,
    setExportSection,
    handleExport,
    exportLoading,
  } = useAnalyticsExport("overview", currentRange, dateRange, selectedGroupId);

  useEffect(() => {
    const onboardingStatusDispatcher = dispatch(
      onboardingStatusAPI({ requestHeaders: userData?.response?.user?._id })
    );
    onboardingStatusDispatcher.then(async (response) => {
      if (response?.payload?.onboarded === false) {
        navigate("/on-boarding", { replace: true });
      } else {
        isLoading(false);
      }
    });
  }, []);

  // Fetch analytics data when date range, currentRange, or group changes
  useEffect(() => {
    if (!selectedGroupId) return;

    setDataLoading(true);

    if (currentRange === "custom") {
      dispatch(
        getAnalyticsOverviewStats({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          group_id: selectedGroupId,
        })
      ).finally(() => {
        setDataLoading(false);
      });
    } else {
      dispatch(
        getAnalyticsOverviewStats({
          range: currentRange,
          group_id: selectedGroupId,
        })
      ).finally(() => {
        setDataLoading(false);
      });
    }
  }, [dispatch, dateRange.startDate, dateRange.endDate, currentRange, selectedGroupId]);

  // Handle date range change
  const handleDateChange = (newDateRange) => {
    // Update Redux state
    dispatch(
      setDateRange({
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
      })
    );
    // API call is handled by useEffect above
  };

  useEffect(() => {
    if (!selectedGroupId) return;
    fetchGraphData(activeTab, currentRange);
  }, [
    activeTab,
    dispatch,
    currentRange,
    dateRange.startDate,
    dateRange.endDate,
    selectedGroupId,
  ]);

  useEffect(() => {
    const map = {
      '7d': '7 days',
      '30d': '30 days',
      '1y': '12 month',
      'custom': 'custom',
      'today': 'today',
      'yesterday': 'yesterday',
    };
    if (map[currentRange]) {
      setActiveTimeFilter(map[currentRange]);
    }
  }, [currentRange]);

  // Fetch heatmap data based on current range
  useEffect(() => {
    if (!selectedGroupId) return;

    if (currentRange === "today") {
      dispatch(getTodayHeatMapStats({ group_id: selectedGroupId }));
    }
    if (currentRange === "yesterday") {
      dispatch(getYesterdayHeatMapStats({ group_id: selectedGroupId }));
    }
    if (currentRange === "7d") {
      dispatch(getWeeklyHeatMapStats({ group_id: selectedGroupId }));
    }
    if (currentRange === "30d") {
      dispatch(getMonthlyHeatMapStats({ group_id: selectedGroupId }));
    }
    if (currentRange === "365d" || currentRange === "1y") {
      dispatch(getYearlyHeatMapStats({ group_id: selectedGroupId }));
    }
    // For custom range, CustomHeatMap component will dispatch with start/end dates
  }, [dispatch, currentRange, selectedGroupId]);

  const fetchGraphData = (tab, range) => {
    if (!selectedGroupId) return;

    // Prepare parameters based on whether custom range is selected
    const getParams = () => {
      if (range === "custom") {
        return {
          range,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          group_id: selectedGroupId,
        };
      }
      return { range, group_id: selectedGroupId };
    };

    const params = getParams();

    switch (tab) {
      case "Total Chats":
        dispatch(getChatGraphStats(params));
        break;
      case "Total Tickets":
        dispatch(getTicketGraphStats(params));
        break;
      case "Chat Satisfaction":
        dispatch(getChatSatisfactionGraphStats(params));
        break;
      case "Website Traffic":
        dispatch(getWebsiteTrafficGraphStats(params));
        break;
      default:
        dispatch(getChatGraphStats(params));
    }
  };

  // Only show percentage change when 7-day filter is selected
  const showPercentageChange = currentRange === "7d";
  const dataArray = [
    {
      title: "Total Visitors",
      number: analyticsOverviewData?.metrics?.totalVisitors?.value || 0,
      textPercentage: showPercentageChange
        ? analyticsOverviewData?.metrics?.totalVisitors?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        (analyticsOverviewData?.metrics?.totalVisitors?.change || 0) >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
    {
      title: "Total Chats",
      number: analyticsOverviewData?.metrics?.totalChats?.value || 0,
      textPercentage: showPercentageChange
        ? analyticsOverviewData?.metrics?.totalChats?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        (analyticsOverviewData?.metrics?.totalChats?.change || 0) >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
    {
      title: "Active Agents",
      number: analyticsOverviewData?.metrics?.activeAgents?.value || 0,
      textPercentage: showPercentageChange
        ? analyticsOverviewData?.metrics?.activeAgents?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        (analyticsOverviewData?.metrics?.activeAgents?.change || 0) >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
    {
      title: "KB Articles Updated",
      number: analyticsOverviewData?.metrics?.kbArticlesUpdated?.value || 0,
      textPercentage: showPercentageChange
        ? analyticsOverviewData?.metrics?.kbArticlesUpdated?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        (analyticsOverviewData?.metrics?.kbArticlesUpdated?.change || 0) >=
          0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
    {
      title: "CSAT Score",
      number: analyticsOverviewData?.metrics?.avgCsat?.value || 0,
      textPercentage: showPercentageChange
        ? analyticsOverviewData?.metrics?.avgCsat?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        (analyticsOverviewData?.metrics?.avgCsat?.change || 0) >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
  ];

  function handleRangeChange(event) {
    const newRange = event.target.value;
    setCurrentRange(newRange);

    // Sync with time filter
    const timeFilterMap = {
      "7d": "7 days",
      "30d": "30 days",
      "1y": "12 month",
    };
    setActiveTimeFilter(timeFilterMap[newRange]);
  }

  const handleTimeFilterChange = (newTimeFilter) => {
    setActiveTimeFilter(newTimeFilter);

    // Sync with range dropdown
    const rangeMap = {
      "7 days": "7d",
      "30 days": "30d",
      "12 month": "1y",
    };
    setCurrentRange(rangeMap[newTimeFilter]);
  };

  const getChartData = () => {
    switch (activeTab) {
      case "Total Chats":
        return (
          chatGraphData?.map((item) => ({
            day: item?.label,
            value: item?.total_chats || 0,
            avgCsat: item.avg_csat || 0,
          })) || []
        );

      case "Total Tickets":
        return (
          ticketGraphData?.map((item) => ({
            day: item?.label,
            primary: item?.new_tickets || 0,
            secondary: item?.closed_tickets || 0,
            value: (item?.new_tickets || 0) + (item?.closed_tickets || 0),
          })) || []
        );

      case "Chat Satisfaction":
        return (
          chatSatisfactionData?.map((item) => ({
            day: item?.label,
            primary: item?.good_count || 0,
            secondary: item?.bad_count || 0,
            value: (item?.good_count || 0) + (item?.bad_count || 0),
            csat: item?.csat || 0,
            totalChats: item?.total_chats || 0,
          })) || []
        );

      case "Website Traffic":
        return (
          websiteTrafficData?.map((item) => ({
            day: item?.label,
            value: item?.totalVisitors || 0,
            newVisitors: item?.newVisitors || 0,
          })) || []
        );

      default:
        return [];
    }
  };

  const getLoadingState = () => {
    switch (activeTab) {
      case "Total Chats":
        return chatGraphLoading;
      case "Total Tickets":
        return ticketGraphLoading;
      case "Chat Satisfaction":
        return chatSatisfactionLoading;
      case "Website Traffic":
        return websiteTrafficLoading;
      default:
        return false;
    }
  };

  return (
    <div className="Dashboard_Main_Containerxx">
      {/* Header Component - Used directly in parent */}
      <MainHeaderAnalytics
        title="Overview"
        dateRange={dateRange}
        onDateChange={handleDateChange}
        exportSectionOptions={exportSectionOptions}
        exportSection={exportSection}
        onExportSectionChange={setExportSection}
        onExport={handleExport}
        exportLoading={exportLoading}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
      />

      {/* Chart Component - Only displays data with loading state */}
      <OverviewChart data={dataArray} loading={dataLoading} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "32px",
          marginBottom: "-6px",
        }}
      >
        <Typography
          sx={{
            fontWeight: "600",
            color: "#061522",
            fontFamily: "Inter",
            fontSize: "24px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "18px",
          }}
        >
          {"All Stats"}
        </Typography>

        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        {/* <TimeFilter
              options={timeOptions}
              activeOption={activeTimeFilter}
              onOptionChange={handleTimeFilterChange}
            /> */}
      </Box>

      {/* Bar Chart below tab navigation */}
      <div style={{ marginBottom: "40px" }}>
        <BarChart
          data={getChartData()}
          selectedTab={activeTab}
          activeTimeFilter={activeTimeFilter}
          loading={getLoadingState()}
        />
      </div>

      <div className="ChatEmail-Container01">
        {currentRange === "today" ? (
          <TodayHeatMap />
        ) : currentRange === "yesterday" ? (
          <YesterdayHeatMap />
        ) : currentRange === "7d" ? (
          <Heatmap groupId={selectedGroupId} />
        ) : currentRange === "30d" ? (
          <MonthlyChatTicketHeatmap groupId={selectedGroupId} />
        ) : currentRange === "365d" || currentRange === "1y" ? (
          <YearlyChatTicketHeatmap groupId={selectedGroupId} />
        ) : currentRange === "custom" ? (
          <CustomHeatMap groupId={selectedGroupId} />
        ) : null}
      </div>
    </div>
  );
}
export default MainAnalyticsOverview;
